import React from "react";
import { useSite } from "@/components/useSite";
import { PublicHeader } from "@/components/PublicHeader";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GraduationCap, MessageCircle, HelpCircle, MapPin, Clock, Mail, Phone, Instagram, Facebook, Youtube, Linkedin, Globe } from "lucide-react";

export default function About() {
  const site = useSite();
  const ct = site?.contato || {};
  const link = (u) => (/^https?:\/\//.test(u) ? u : `https://${u.replace(/^@/, "instagram.com/")}`);
  const redes = [[Instagram, "Instagram", ct.instagram], [Facebook, "Facebook", ct.facebook], [Youtube, "YouTube", ct.youtube], [Linkedin, "LinkedIn", ct.linkedin], [Globe, "Site", ct.website]].filter((r) => (r[2] || "").trim());
  const temContato = ct.address || ct.hours || ct.phone || ct.support_email || ct.cnpj || redes.length > 0;

  const faqs = site?.faqs || [];
  const wpp = (site?.whatsapp || "").replace(/\D/g, "");

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <PublicHeader />

      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg,#1A2340,#232D4F 55%,#1E9E2A)" }}>
        <img src="/brand/mockup01.webp" alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="relative z-10 max-w-4xl mx-auto px-5 py-16 text-center">
          <div className="inline-flex bg-white rounded-xl px-3 py-2 shadow-md mb-6"><img src="/brand/logo-horizontal.webp" alt="IEP – In'Nova Educação Profissional" className="h-9 w-auto" /></div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">Sobre a instituição</h1>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-5 py-14 space-y-14">
        <section>
          <div className="flex items-center gap-2 mb-4">
            <GraduationCap className="h-5 w-5 text-[#1E9E2A]" />
            <h2 className="font-display text-2xl font-bold text-slate-900">Quem somos</h2>
          </div>
          <p className="text-slate-600 leading-relaxed whitespace-pre-line">{site?.about}</p>
          {wpp && (
            <a href={`https://wa.me/55${wpp}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-6 text-[#1E9E2A] font-semibold hover:underline">
              <MessageCircle className="h-4 w-4" /> Fale conosco no WhatsApp
            </a>
          )}
        </section>

        {temContato && (
          <section className="bg-white rounded-2xl border border-slate-200 p-6 space-y-3" data-testid="sobre-contato">
            <h2 className="font-display text-xl font-bold text-slate-900">Onde estamos</h2>
            <div className="grid sm:grid-cols-2 gap-3 text-sm text-slate-600">
              {ct.address && <div className="flex gap-2"><MapPin className="h-4 w-4 mt-0.5 text-[#1E9E2A] shrink-0" />{ct.address}</div>}
              {ct.hours && <div className="flex gap-2"><Clock className="h-4 w-4 mt-0.5 text-[#1E9E2A] shrink-0" />{ct.hours}</div>}
              {ct.phone && <div className="flex gap-2"><Phone className="h-4 w-4 mt-0.5 text-[#1E9E2A] shrink-0" />{ct.phone}</div>}
              {ct.support_email && <div className="flex gap-2"><Mail className="h-4 w-4 mt-0.5 text-[#1E9E2A] shrink-0" />{ct.support_email}</div>}
            </div>
            {redes.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {redes.map(([Ic, nome, u]) => <a key={nome} href={link(u)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-[#232D4F] hover:bg-slate-50"><Ic className="h-4 w-4" /> {nome}</a>)}
              </div>
            )}
            {(ct.name || ct.cnpj) && <p className="text-xs text-slate-400 pt-1">{[ct.name, ct.cnpj && `CNPJ ${ct.cnpj}`].filter(Boolean).join(" · ")}</p>}
          </section>
        )}

        <section>
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="h-5 w-5 text-[#232D4F]" />
            <h2 className="font-display text-2xl font-bold text-slate-900">Perguntas frequentes</h2>
          </div>
          {faqs.length === 0 ? (
            <p className="text-slate-400 text-sm">Em breve.</p>
          ) : (
            <Accordion type="single" collapsible className="space-y-2">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`f${i}`} className="bg-white rounded-xl border border-slate-200 px-4">
                  <AccordionTrigger className="text-left font-medium text-slate-800 hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white py-8 text-center text-sm text-slate-400">
        {new Date().getFullYear()} © IEP – In&apos;Nova Educação Profissional — Desenvolvido por <strong>In&apos;Nova Art and Sciences</strong> | Todos os direitos reservados
      </footer>
    </div>
  );
}
